// Canvas.js (fixed refs)
import React, { useState, useEffect, useRef, useMemo, forwardRef, useCallback } from 'react';
import useResponsiveCanvasSize from '../hooks/useResponsiveCanvasSize';
import '../style/Canvas.css';
import LegendDisplay from './personalize/LegendDisplay';
import { getRandomColor } from '../utils/colorUtils';
import { useIsSquare } from "../hooks/useIsSquare";
import useScreenSize from '../hooks/useScreenSize';
import {
  getRadiusForLayer,
  generateGradientDef,
  generateShapes
} from '../utils/svgUtils';

import ZoomToggle from './canvas/ZoomToggle';
import CanvasSVG from './canvas/CanvasSVG';

/* -------------------- ZoomPanArea (child) -------------------- */
const ZoomPanArea = forwardRef((props, forwardedRef) => {
  const {
    step,
    frameSettings,
    otherSettings,
    bgStyle,
    framePadding = 0,
    isSquare,
    zoom = 100,
    fixedCanvasSize = 0,
    backgroundImageStyle = '',
    backgroundSize = undefined,
    children,
    classNameOptional = '',
  } = props;

  // Always create an internal ref (hooks must be unconditional).
  const innerRef = useRef(null);
  const { isMobile } = useScreenSize();

  // Provide setter to assign both innerRef and forwardedRef correctly.
  const setRef = (el) => {
    innerRef.current = el;
    if (!forwardedRef) return;
    if (typeof forwardedRef === 'function') {
      try { forwardedRef(el); } catch (e) { /* ignore */ }
    } else {
      forwardedRef.current = el;
    }
  };

  // Pan state (CSS pixels)
  const [pan, setPan] = useState({ x: 0, y: 0 });
  const draggingRef = useRef(false);
  const startPtrRef = useRef({ x: 0, y: 0 });
  const startPanRef = useRef({ x: 0, y: 0 });
  const [isDragging, setIsDragging] = useState(false);

  // compute effective zoomScale (matches earlier behaviour: scale = zoom/100, non-square *0.8)
  const zoomScale = useMemo(() => {
    const base = zoom / 100;
    return (isSquare && frameSettings?.orientation === 'square') ? base : base * 0.8;
  }, [zoom, isSquare, frameSettings]);

  // clamp helper and simple pan bounds heuristic
  const clamp = (v, min, max) => Math.max(min, Math.min(max, v));
  const maxPanOffset = useMemo(() => {
    return Math.max(0, (zoomScale - 1) * fixedCanvasSize / 2);
  }, [zoomScale, fixedCanvasSize]);

  // pointer handlers on the frame-container (use innerRef)
  useEffect(() => {
    const el = innerRef.current;
    if (!el) return;

    const onPointerDown = (ev) => {
      if (zoomScale <= 1) return;
      if (ev.pointerType === 'mouse' && ev.button !== 0) return;

      try { el.setPointerCapture(ev.pointerId); } catch (e) {}
      draggingRef.current = true;
      setIsDragging(true);
      startPtrRef.current = { x: ev.clientX, y: ev.clientY };
      startPanRef.current = { ...pan };
    };

    const onPointerMove = (ev) => {
      if (!draggingRef.current) return;
      const dx = ev.clientX - startPtrRef.current.x;
      const dy = ev.clientY - startPtrRef.current.y;
      const newX = startPanRef.current.x + dx;
      const newY = startPanRef.current.y + dy;
      const clampedX = clamp(newX, -maxPanOffset, maxPanOffset);
      const clampedY = clamp(newY, -maxPanOffset, maxPanOffset);
      setPan({ x: clampedX, y: clampedY });
    };

    const onPointerUp = (ev) => {
      if (draggingRef.current) {
        draggingRef.current = false;
        setIsDragging(false);
        try { el.releasePointerCapture(ev.pointerId); } catch (e) {}
      }
    };

    const onPointerCancel = () => {
      if (draggingRef.current) {
        draggingRef.current = false;
        setIsDragging(false);
      }
    };

    el.addEventListener('pointerdown', onPointerDown);
    window.addEventListener('pointermove', onPointerMove);
    window.addEventListener('pointerup', onPointerUp);
    el.addEventListener('pointercancel', onPointerCancel);

    return () => {
      el.removeEventListener('pointerdown', onPointerDown);
      window.removeEventListener('pointermove', onPointerMove);
      window.removeEventListener('pointerup', onPointerUp);
      el.removeEventListener('pointercancel', onPointerCancel);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [zoomScale, maxPanOffset, fixedCanvasSize, pan]);

  // Reset or clamp pan when zoom changes
  useEffect(() => {
    if (zoomScale <= 1) {
      setPan({ x: 0, y: 0 });
    } else {
      setPan(prev => ({
        x: clamp(prev.x, -maxPanOffset, maxPanOffset),
        y: clamp(prev.y, -maxPanOffset, maxPanOffset),
      }));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps 
  }, [zoomScale, maxPanOffset]);


  // Build the base frame-container style (same logic your Canvas had previously), then apply pan & cursor
  const paddingValue = isMobile ? 0.5 : 1;
  const zoomValue = 0.8;
  const baseStyle = {
    zoom: zoom / 100 * zoomValue, //(isSquare && frameSettings?.orientation === 'square') ? zoom / 100 : zoom / 100 * 0.8,
    backgroundImage: backgroundImageStyle || undefined,
    backgroundSize: backgroundSize || undefined,
    backgroundPosition: 'center',
    backgroundRepeat: 'no-repeat',
    backgroundColor: (!otherSettings?.useBleed && step > 4 && frameSettings?.frame !== 'none') ? (otherSettings?.backgroundColor || '#fff') : undefined,
    padding:
    step > 4
      ? frameSettings?.material === 'canvas'
        ? ''
        : (isSquare && frameSettings?.orientation === 'square')
          ? `${framePadding*paddingValue}px`
          : `${framePadding*paddingValue}px ${Math.round(framePadding *paddingValue)-1}px`
      : '',
  };

  // Apply pan as transform and cursor styles
  const mergedStyle = {
    ...baseStyle,
    transform: `translate(${pan.x}px, ${pan.y}px)`,
    cursor: isDragging ? 'grabbing' : (zoomScale > 1 ? 'grab' : undefined),
    touchAction: zoomScale > 1 ? 'none' : undefined,
  };

  // Build the className exactly as before so CSS rules match
  const wrapperClassName = `frame-container material-${frameSettings?.material} 
      ${step < 3 ? '' : (isSquare && frameSettings?.orientation === 'square') ? 'square' : 'portrait'} 
      ${(step === 5 && frameSettings?.frame !== 'none' && frameSettings?.material !== 'canvas') ? 'frame-shadow' : ''} 
      size-${frameSettings?.size} ${classNameOptional}`;

  return (
    <div
      ref={setRef}
      className={wrapperClassName}
      onContextMenu={(e) => e.preventDefault()}
      style={mergedStyle}
    >
      {children}
    </div>
  );
});

ZoomPanArea.displayName = 'ZoomPanArea';

/* -------------------- Canvas (parent) -------------------- */

const Canvas = ({ layerSettings, setLayerSettings, step, personalSettings, otherSettings, frameSettings, frameOptions, formattedDate }) => {
  const fixedCanvasSize = useResponsiveCanvasSize();
  const isSquare = useIsSquare(personalSettings);
  const { isMobile, isSmallScreen } = useScreenSize();
  const [renderKey, setRenderKey] = useState(0);
  const bgStyle = otherSettings.style || 'dark';

  // Compute max radius & related offsets as before
  const maxRadius = Math.max(...layerSettings.map(item => item.shapeRadius));
  const shapeSize = (maxRadius / 100) * fixedCanvasSize;
  const topOffset = (fixedCanvasSize - shapeSize) / 2;
  const scaleFont = isMobile ?  0.6 : 1;
  // Compute boxShadow separately (same logic)
  const boxShadowValue = useMemo(() => {
    if (step < 5) return 'inset';
    if (step === 5) {
      if (frameSettings.material === 'canvas') return 'none';
      if (frameSettings.frame !== 'none') return 'none';
      return 'inset';
    }
    return 'none';
  }, [step, frameSettings.material, frameSettings.frame]);

  // --- CHANGED: use callback ref + canvasNode to ensure observer re-attaches on remounts
  const [canvasNode, setCanvasNode] = useState(null);
  const [actualCanvasWidth, setActualCanvasWidth] = useState(0);

  const INFO_DESIGN_WIDTH = 800;

  const infoZoom = useMemo(() => {
    if (!actualCanvasWidth) return 1;
    const raw = actualCanvasWidth / INFO_DESIGN_WIDTH;
    return Math.min(1, Math.max(0.6, raw));
  }, [actualCanvasWidth]);

  const canvasRef = useCallback((node) => {
    setCanvasNode(node);
  }, []);

  useEffect(() => {
    // boxShadow manipulation uses the current canvas DOM node (if present)
    if (!canvasNode) return;
    canvasNode.style.boxShadow = '';
    const t = setTimeout(() => {
      canvasNode.style.boxShadow = boxShadowValue;
    }, 10);
    return () => clearTimeout(t);
  }, [boxShadowValue, canvasNode]);

  useEffect(() => {
    if (!canvasNode) {
      setActualCanvasWidth(0);
      return;
    }

    const observer = new ResizeObserver(entries => {
      for (let entry of entries) {
        setActualCanvasWidth(entry.contentRect.width);
      }
    });

    observer.observe(canvasNode);

    // initialize with current size immediately (fast fallback)
    setActualCanvasWidth(canvasNode.getBoundingClientRect().width || canvasNode.offsetWidth || 0);

    return () => observer.disconnect();
  }, [canvasNode]);

  const frameWidthInInches = useMemo(() => {
    if (!frameSettings?.size) return 12; // fallback
    const [w] = frameSettings.size.split('x').map(Number);
    return w || 1;
  }, [frameSettings.size]);

  const computedPadding = useMemo(() => {
    if (!actualCanvasWidth || !frameWidthInInches) return 0;
    return actualCanvasWidth / frameWidthInInches; // px per inch
  }, [actualCanvasWidth, frameWidthInInches]);

  useEffect(() => {
    setRenderKey((prevKey) => prevKey + 1);
  }, [layerSettings]);

  const canvasStyleBase = useMemo(() => {
    return {
      width: fixedCanvasSize,
      height:
        (!isSquare || frameSettings.orientation === 'portrait') && step >= 3
          ? fixedCanvasSize + fixedCanvasSize * 0.33
          : fixedCanvasSize,
      backgroundColor: otherSettings.useBleed ? otherSettings.backgroundColor : undefined,
      padding: `${computedPadding}px`,
    };
  }, [
    step,
    frameSettings.orientation,
    otherSettings.useBleed,
    otherSettings.backgroundColor,
    fixedCanvasSize,
    isSquare,
    computedPadding, // <-- added to deps so padding updates correctly
  ]);
  // zoom logic (unchanged)
  const initialZoom = 100;
  const [zoom, setZoom] = useState(initialZoom);
  const intervalRef = useRef(null);
  const changeZoom = (delta) => {
    setZoom((prev) => {
      let newZoom = prev + delta;
      if (isMobile && newZoom > 250) {
        newZoom = 100;
      }
      return Math.max(100, Math.min(1000, newZoom));
    });
  };
  const startZooming = (delta) => {
    changeZoom(delta);
    intervalRef.current = setInterval(() => {
      changeZoom(delta);
    }, 100);
  };
  const stopZooming = () => {
    clearInterval(intervalRef.current);
    intervalRef.current = null;
  };
  const isZoomInDisabled = zoom >= 1000;
  const isZoomOutDisabled = zoom <= 100;

  useEffect(() => {
    // stop any continuous zooming
    stopZooming?.();
    // reset to default zoom
    setZoom(initialZoom);
  }, [step]);
  // --- frame image utilities (unchanged)
  function getFrameImageUrl(frames = [], frameId, orientation, size) {
    if (!frameId || frameId === 'none') return null;

    const frame = Array.isArray(frames) ? frames.find(f => (f.id ?? '') === frameId) : null;
    if (!frame) {
      console.warn(`[Canvas] Frame with ID ${frameId} not found`);
      return null;
    }

    const sizesMap = frame.sizes || {};
    if (!orientation || !size) {
      console.warn(`[Canvas] Missing orientation or size to pick image for frame ${frameId}`);
      return null;
    }

    const orientMap = sizesMap[orientation];
    if (!orientMap || typeof orientMap !== 'object') {
      console.warn(`[Canvas] Frame ${frameId} has no sizes configured for orientation "${orientation}"`);
      return null;
    }

    const sizeEntry = orientMap[size];
    if (!sizeEntry) {
      console.warn(`[Canvas] Frame ${frameId} has no image for size "${size}" in orientation "${orientation}"`);
      return null;
    }

    let url = null;
    let padding = (typeof sizeEntry.padding === 'number') ? sizeEntry.padding : (sizeEntry.padding ? Number(sizeEntry.padding) : 0);

    if (typeof sizeEntry === 'string') {
      url = sizeEntry;
    } else if (typeof sizeEntry === 'object' && sizeEntry !== null) {
      url = sizeEntry.url ?? null;
    }

    if (!url) {
      console.warn(`[Canvas] Frame ${frameId} size ${size} has no url configured`);
      return null;
    }

    return { url, padding: (isNaN(padding) ? 0 : padding) };
  }

  function getSizePadding(globalSizes = {}, sizeId) {
    if (!globalSizes || typeof globalSizes !== 'object') return null;
    for (const orientKey of Object.keys(globalSizes)) {
      const list = globalSizes[orientKey];
      if (!Array.isArray(list)) continue;
      for (const s of list) {
        if ((s.id ?? '') === sizeId) {
          return (typeof s.padding === 'number') ? s.padding : (s.padding ? Number(s.padding) : null);
        }
      }
    }
    return null;
  }

  const currentSizeId = (typeof frameSettings.size === 'string') ? frameSettings.size : '';
  const selectedSize = useMemo(() => {
    if (!currentSizeId) return [0, 0];
    const parts = currentSizeId.split('x').map(p => Number(p));
    if (parts.length >= 2) return parts;
    return [0, 0];
  }, [currentSizeId]);

  // Compute frame image object and padding
  const frameImageObj = (step > 3)
    ? getFrameImageUrl(frameOptions.frames, frameSettings.frame, frameSettings.orientation, frameSettings.size)
    : null;

  const framePadding = frameImageObj?.padding ?? getSizePadding(frameOptions.sizes, frameSettings.size) ?? 0;
  const backgroundImageStyle = (step > 4 && frameImageObj && frameImageObj.url) ? `url(${frameImageObj.url})` : '';
  const backgroundSize = (backgroundImageStyle ? 'cover' : undefined);

  // frameRef used for boxShadow manipulations (we forward into ZoomPanArea via forwardedRef)
  const frameRef = useRef(null);

  return (
    <>
      {step > 1 && (
        <ZoomToggle
          isZoomInDisabled={isZoomInDisabled}
          isZoomOutDisabled={isZoomOutDisabled}
          startZoomIn={() => !isZoomInDisabled && startZooming(10)}
          startZoomOut={() => !isZoomOutDisabled && startZooming(-100)}
          stopZoom={stopZooming}
          zoom={zoom}
          step={step}
          layerSettings={layerSettings}
          formattedDate={formattedDate}
          personalSettings={personalSettings}
          isSquare={isSquare} // prefer the prop from parent if provided
          otherSettings={otherSettings}
          frameSettings={frameSettings}
        />
      )}
      <ZoomPanArea
        ref={frameRef}
        step={step}
        frameSettings={frameSettings}
        otherSettings={otherSettings}
        bgStyle={bgStyle}
        framePadding={framePadding}
        isSquare={isSquare}
        zoom={zoom}
        fixedCanvasSize={fixedCanvasSize}
        backgroundImageStyle={backgroundImageStyle}
        backgroundSize={backgroundSize}
      >
        {/* canvas-product-mask (same as before) */}
        {(frameSettings.material === 'canvas' && step > 4) && (
          <div className={`canvas-product-mask ${bgStyle}`}
            style={{
               backgroundColor: otherSettings.useBleed ? otherSettings.backgroundColor : "#fff"
            }}
          >
            <div className="top-left corner" ></div>
            <div className="top-right corner"></div>
            <div className="bottom-right corner"></div>
            <div className="bottom-left corner"></div>
          </div>
        )}

        <div
          key={step}
          ref={canvasRef}
          style={canvasStyleBase}
          className={`canvas ${bgStyle}${!otherSettings.useBleed ? '-bleed' : ''} ${(frameSettings.material === 'canvas' && step > 4)? 'dashed-border' : ''}`}
        >
          {step > 4 && (
            <>
              {!isMobile && (<div className="vertical-dimension"
                style={{
                  width:
                    (!isSquare || frameSettings.orientation === 'portrait') && step >= 3
                    ? fixedCanvasSize + (fixedCanvasSize * 0.33)
                    : fixedCanvasSize,
                }}
              >
                <span>{selectedSize[1]} Inches</span></div>)}
              <div className="horizontal-dimension"
                style={{
                  width: fixedCanvasSize,
                  marginTop:(selectedSize[1]>24)? '-16px': ''
                }}
              ><span>{isMobile? selectedSize[0] + 'x' + selectedSize[1]: selectedSize[0]} Inches</span></div>
            </>
          )}

          <div className={bgStyle} style={{
            backgroundColor: (otherSettings.style === "color" || otherSettings.style === "color-border")
              ? otherSettings.backgroundColor
              : undefined
          }}>
            <div
              className='inner-wrapper'
              style={{
                display : (isSquare && frameSettings.orientation === 'portrait')? 'flex':'block'
              }}
            >
              <CanvasSVG
                key={renderKey}
                renderKey={renderKey}
                fixedCanvasSize={fixedCanvasSize}
                layerSettings={layerSettings}
                getRadiusForLayer={getRadiusForLayer}
                generateGradientDef={generateGradientDef}
                generateShapes={generateShapes}
                getRandomColor={getRandomColor}
              />

              {(step >= 3 && !isSquare) && (
                <div className='info-wrapper'
                  style={{
                   marginTop: `-${topOffset - 50}px`,
                    justifyContent: personalSettings.showLegend ? 'center' : 'flex-end',
                    transform: `scale(${infoZoom})`,
                    transformOrigin: 'top center',
                  }}
                >
                  {personalSettings.showTitle && (
                    <div
                      className='title-wrapper'
                      style={{
                        fontFamily: personalSettings.titleFontFamily,
                        fontSize: `${Math.round(personalSettings.titleFontSize*scaleFont)}px`,
                        color: (otherSettings?.style === 'dark' || 'dark-border') ? 'white' : 'black',
                        lineHeight: `${Math.round(personalSettings.titleFontSize*scaleFont)}px`,
                        height: `${Math.round(personalSettings.titleFontSize*scaleFont)}px`,
                      }}
                    >
                      {personalSettings.titleText}
                    </div>
                  )}

                  {personalSettings.showSubtitle && (
                    <div
                      className='subTitle-wrapper'
                      style={{
                        fontFamily: personalSettings.subtitleFontFamily,
                        fontSize: `${Math.round(personalSettings.subtitleFontSize*scaleFont)}px`,
                        color: (otherSettings?.style === 'dark' || 'dark-border') ? 'white' : 'black',
                        lineHeight: `${Math.round(personalSettings.subtitleFontSize*scaleFont)}px`,
                        height: `${Math.round(personalSettings.subtitleFontSize*scaleFont)}px`,
                      }}
                    >
                      {personalSettings.subtitleText}
                    </div>
                  )}

                  {personalSettings.showLegend && (
                    <div className='legend-wrapper'>
                      <LegendDisplay layerSettings={layerSettings} size={50} spacing={50*infoZoom} />
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>
        </div>
      </ZoomPanArea>
    </>
  );
};

export default Canvas;
